//-------------------------------------------------------------------------
// ZoneDevicePanel
//-------------------------------------------------------------------------
// Author: Robert Rolland (robert.rolland@nuvoola.com)
// Date:   Thurday, July 21, 2022
//-------------------------------------------------------------------------
// This component is intended to organize the fields and attributes used in
// viewing and editing a device.



import React from "react";
import { connect } from "react-redux";
import { get, map, sortBy, filter, size, toLower, trim } from "lodash";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";

import SectionTitle from "../Common/SectionTitle";
import AttributeSection from "../Common/AttributeSection";

import { ReactComponent as YesIcon } from "../../assets/img/common/check_green.svg";
import { ReactComponent as NoIcon } from "../../assets/img/common/red_cross_circle.svg";

import { getAttributeValue } from "../../utils/profile";
import { getLov } from "../../utils/lov";

const styles = {
  enableIcon: {
    height: "14px",
    width: "15px",
    margin: "0 2px -1px 0"
  }
};

const DEVICE_TYPE_ARCTURUS = 12;

const ZoneDeviceForm = (props) => {
  const { classes, language,
    isEditMode,
    handleChange,
    values,
    valid,
    arcturusCloud,
    arcturusService, 
    deviceAttr,
    isDialog
  } = props;

  const deviceTypeId = get(values, ['device_type_id']);
  const [deviceTypes, setDeviceTypes] = React.useState();

  React.useEffect(() => {
    const fetchType = async () => {
      const types = await getLov("deviceTypes");
      setDeviceTypes(types);
    };
    fetchType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const secondaryAttr = sortBy(
    filter(deviceAttr, (attr) => attr.active && !attr.app_display_main_page &&
      (deviceTypeId !== DEVICE_TYPE_ARCTURUS || ![274, 275, 276, 279].includes(attr.id))),
    (attr) => get(attr, ["app_display_seq_numb"])
  );
  
  return (
    <Grid container direction="column" justify="flex-start">
      <Grid item xs={12} style={{ maxWidth: "600px", padding: 0, margin:0, flexWrap: "nowrap" }} container spacing={1}>
        <Grid item xs={size(secondaryAttr) > 0 ? 6 : 12} container direction="column">
          {/* First or only column (depends on device type - some have no secondary attributes) */}
          { isEditMode && /* Edit name goes here */
            <AttributeSection
              title="name"
              id="name"
              values={[get(values, ["name"])]}
              setFormValue={handleChange}
              options={{
                required: true,
                redBorder: !get(valid, ["name"]),
                autocomplete: "off",
              }}
              isEditMode={isEditMode}
            />
          }
          <AttributeSection
            title2={I18n.get("companies_deviceType")}
            id={"device_type_id"}
            values={isEditMode ? [get(values, ["device_type_id"])] : [get(values, [`device_type_name_${language}`])]}
            setFormValue={handleChange}
            type="select"
            options={{
              disabled: !isDialog,
              required: true,
              redBorder: !get(valid, ["device_type_id"]),
              options: map(deviceTypes, (type) => ({
                id: get(type, ["id"]),
                name: get(type, [`name_${language}`]),
              })),
            }}
            isEditMode={isEditMode}
          />
          {map(
            sortBy(
              filter(
                deviceAttr,
                (attr) =>
                  attr.active &&
                  attr.app_display_main_page &&
                  ![20, 21, 22, 23, 24, 25, 26, 27].includes(
                    attr.id
                  )
              ),
              (attr) => get(attr, ["app_display_seq_numb"])
            ),
            (attr) => (
              <AttributeSection
                key={attr.id}
                attribute={attr}
                id={attr.id}
                values={[getAttributeValue(values, attr.id)]}
                setFormValue={handleChange}
                isEditMode={isEditMode}
              />
            )
          )}
        </Grid>
        { size(secondaryAttr) > 0 &&
          <Grid item xs={6} container direction="column">
          {/* Second column */}
          {map(
            sortBy(
              filter(
                deviceAttr,
                (attr) => attr.active && !attr.app_display_main_page &&
                ![274, 275, 276, 279].includes(attr.id)
              ),
              (attr) => get(attr, ["app_display_seq_numb"])
            ),
            (attr) => (
              <AttributeSection
                key={attr.id}
                attribute={attr}
                id={attr.id}
                values={[getAttributeValue(values, attr.id)]}
                setFormValue={handleChange}
                isEditMode={isEditMode}
              />
            )
          )}
        </Grid>
      }
      </Grid>
      { deviceTypeId === DEVICE_TYPE_ARCTURUS && <>
        {/* Arcturus Cloud Section (only if device is an arturus vending machine) */}
        <Grid item xs={12} container>
          <SectionTitle>
            {I18n.get('arcturus_cloud')}
          </SectionTitle>
        </Grid>
        <Grid item xs style={{ maxWidth: "600px", padding: "4px", flexWrap: "nowrap" }} container spacing={1}>
          <Grid item xs={6} container direction="column">
            {/* First column */}
            <Grid item style={{ padding: "8px", maxWidth: "100%", ...props.style }}>{/* Arcturus Cloud Service Status*/}
              <Box className={"text-87 bold-500"}>
                {I18n.get("arcturus_status")}
              </Box>
              { ![get(arcturusService, ["enabled"])] || [get(arcturusService, ["enabled"])].length === 0 ? (
                <><NoIcon className={classes.enableIcon} /><span className="text-54">{I18n.get("enabled_no")}</span></>
              ) : (
                map([get(arcturusService, ["enabled"])], (item, index) => (
                  <Grid
                    container
                    key={item + index}
                    wrap="nowrap"
                    style={{
                      marginBottom: values.length > 0 ? "8px" : "0px",
                      overflowWrap: "break-word",
                    }}
                  >
                    <Grid
                      item
                      style={{ overflow: "auto" }}
                      data-automation={`data_field_attribute_${toLower(trim(I18n.get("arcturus_status")))}`}
                    >
                      {typeof item === "boolean" && item ? (
                        <><YesIcon className={classes.enableIcon} /><span className="text-54">{I18n.get("enabled_yes")}</span></>
                      ) : typeof item === "boolean" && !item ? (
                        <><NoIcon className={classes.enableIcon} /><span className="text-54">{I18n.get("enabled_no")}</span></>
                      ) : (
                        <><NoIcon className={classes.enableIcon} /><span className="text-54">{I18n.get("enabled_no")}</span></>
                      )}
                    </Grid>
                  </Grid>
                ))
              )}
            </Grid>
            {map(
              sortBy(
                filter(
                  deviceAttr,
                  (attr) => attr.active && !attr.app_display_main_page &&
                  [274].includes(attr.id)
                ),
                (attr) => get(attr, ["app_display_seq_numb"])
              ),
              (attr) => (
                <AttributeSection
                  key={attr.id}
                  attribute={attr}
                  id={attr.id}
                  values={isEditMode ? [getAttributeValue(values, attr.id)] : [get(get(get(filter(get(arcturusCloud, ["config"]), (config) => get(config, "id") === getAttributeValue(values, attr.id)), [0]), ["name"]), [language])]}
                  setFormValue={handleChange}
                  type="select"
                  options={{
                    options: map(get(arcturusCloud, ["config"]), (config) => ({
                      id: get(config, ["id"]),
                      name: get(get(config, ["name"]), [language]),
                    })),
                  }}
                  isEditMode={isEditMode}
                />
              )
            )}
            {map(
              sortBy(
                filter(
                  deviceAttr,
                  (attr) => attr.active && !attr.app_display_main_page &&
                  [275].includes(attr.id)
                ),
                (attr) => get(attr, ["app_display_seq_numb"])
              ),
              (attr) => (
                <AttributeSection
                  key={attr.id}
                  attribute={attr}
                  id={attr.id}
                  values={[getAttributeValue(values, attr.id)]}
                  setFormValue={handleChange}
                  isEditMode={isEditMode}
                />
              )
            )}
            {map(
              sortBy(
                filter(
                  deviceAttr,
                  (attr) => attr.active && !attr.app_display_main_page &&
                  [276].includes(attr.id)
                ),
                (attr) => get(attr, ["app_display_seq_numb"])
              ),
              (attr) => (
                <AttributeSection
                  key={attr.id}
                  attribute={attr}
                  id={attr.id}
                  values={[getAttributeValue(values, attr.id)]}
                  setFormValue={handleChange}
                  isEditMode={isEditMode}
                />
              )
            )}
          </Grid>
          <Grid item xs={6} container direction="column">
            {/* Second column */}
            {map(
              sortBy(
                filter(
                  deviceAttr,
                  (attr) => attr.active && !attr.app_display_main_page &&
                  [279].includes(attr.id)
                ),
                (attr) => get(attr, ["app_display_seq_numb"])
              ),
              (attr) => (
                <AttributeSection
                  key={attr.id}
                  attribute={attr}
                  id={attr.id}
                  values={[getAttributeValue(values, attr.id)]}
                  setFormValue={handleChange}
                  isEditMode={isEditMode}
                />
              )
            )}
          </Grid>
        </Grid>
      </>}
      { (!isDialog && false) && <>{/* TODO: remove false when events are available */}
        {/* Events section (not visible on create dialog) */}
        <Grid item xs={12} container>
          <SectionTitle>
            {I18n.get('profiles_events')}
          </SectionTitle>
        </Grid>
        {/* TODO: Table of events with this device goes here */}
      </>}
    </Grid>
  );
}


const mapStateToProps = (state) => {
  return {
    language: get(state, ["user", "language"])
  };
};
  
export default connect(mapStateToProps)(withStyles(styles)(ZoneDeviceForm));
